<template>
  <div
    v-if="shouldRender"
    :class="wrapperClasses"
    @click="handleOverlayClick"
  >
    <div
      :class="cardClasses"
      @click.stop
      v-motion
      :initial="{ opacity: 0, scale: 0.8 }"
      :enter="{ opacity: 1, scale: 1 }"
      :leave="{ opacity: 0, scale: 0.8 }"
      :duration="300"
    >
      <!-- Header -->
      <div class="flex justify-between items-center mb-6 border-b border-gray-200 pb-4">
        <component
          :is="isPage ? 'h1' : 'h2'"
          class="text-3xl font-poppins font-bold text-dark-gray"
        >Privacy Policy</component>
        <button
          v-if="!isPage"
          @click="closeModal"
          class="w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors duration-200"
        >
          <X :size="20" class="text-dark-gray" />
        </button>
      </div>

      <!-- Content -->
      <div class="prose prose-gray max-w-none">
        <p class="text-sm text-gray-500 mb-6">Last updated: {{ lastUpdated }}</p>
        
        <section class="mb-8">
          <h3 class="text-xl font-semibold text-dark-gray mb-4">1. Information We Collect</h3>
          <p class="text-dark-gray/80 mb-4">
            At Homvisit, we collect information you provide directly to us, such as when you:
          </p>
          <ul class="list-disc pl-6 text-dark-gray/80 space-y-2">
            <li>Request our home blood collection services</li>
            <li>Book corporate medical camps</li>
            <li>Contact us through our website or phone</li>
            <li>Subscribe to our newsletters or updates</li>
          </ul>
        </section>

        <section class="mb-8">
          <h3 class="text-xl font-semibold text-dark-gray mb-4">2. How We Use Your Information</h3>
          <p class="text-dark-gray/80 mb-4">
            We use the information we collect to:
          </p>
          <ul class="list-disc pl-6 text-dark-gray/80 space-y-2">
            <li>Provide and improve our healthcare services</li>
            <li>Schedule appointments and coordinate service delivery</li>
            <li>Communicate with you about your health services</li>
            <li>Send you important updates about your tests and results</li>
            <li>Comply with legal and regulatory requirements</li>
          </ul>
        </section>

        <section class="mb-8">
          <h3 class="text-xl font-semibold text-dark-gray mb-4">3. Information Sharing</h3>
          <p class="text-dark-gray/80 mb-4">
            We do not sell, trade, or otherwise transfer your personal information to third parties except:
          </p>
          <ul class="list-disc pl-6 text-dark-gray/80 space-y-2">
            <li>With your explicit consent</li>
            <li>To healthcare providers involved in your care</li>
            <li>When required by law or legal process</li>
            <li>To protect our rights and safety</li>
          </ul>
        </section>

        <section class="mb-8">
          <h3 class="text-xl font-semibold text-dark-gray mb-4">4. Data Security</h3>
          <p class="text-dark-gray/80">
            We implement appropriate security measures to protect your personal information against 
            unauthorized access, alteration, disclosure, or destruction. This includes physical, 
            electronic, and procedural safeguards.
          </p>
        </section>

        <section class="mb-8">
          <h3 class="text-xl font-semibold text-dark-gray mb-4">5. Your Rights</h3>
          <p class="text-dark-gray/80 mb-4">
            You have the right to:
          </p>
          <ul class="list-disc pl-6 text-dark-gray/80 space-y-2">
            <li>Access your personal information</li>
            <li>Correct inaccurate information</li>
            <li>Request deletion of your information</li>
            <li>Opt-out of marketing communications</li>
          </ul>
        </section>

        <section class="mb-8">
          <h3 class="text-xl font-semibold text-dark-gray mb-4">6. LinkedIn application</h3>
          <p class="text-dark-gray/80">
            If you authorize Homvisit's LinkedIn application, the data LinkedIn shares with us is
            described in the
            <RouterLink to="/linkedin-privacy-policy" class="text-light-purple hover:underline">LinkedIn Privacy Policy</RouterLink>.
          </p>
        </section>

        <section class="mb-8">
          <h3 class="text-xl font-semibold text-dark-gray mb-4">7. Contact Us</h3>
          <p class="text-dark-gray/80">
            If you have questions about this Privacy Policy, please contact us at:
          </p>
          <div class="mt-4 p-4 bg-light-purple/10 rounded-lg">
            <p class="text-dark-gray font-medium">Email: sujithvijayan@homvisit.com</p>
            <p class="text-dark-gray font-medium">Phone: 75 9409 9409</p>
            <p class="text-dark-gray font-medium">Address: Homvisit, Trivandrum, Kerala</p>
          </div>
        </section>
      </div>
    </div>
  </div>
</template>

<script>
import { computed } from 'vue'
import { X } from 'lucide-vue-next'
import {
  breadcrumbSchema,
  graphSchema,
  organizationSchema,
  useSeo,
} from '../composables/useSeo'

export default {
  name: 'PrivacyPolicy',
  components: {
    X
  },
  props: {
    isVisible: {
      type: Boolean,
      default: false
    },
    isPage: {
      type: Boolean,
      default: false
    }
  },
  emits: ['close'],
  setup(props, { emit }) {
    const lastUpdated = computed(() => new Date().toLocaleDateString())
    const shouldRender = computed(() => props.isPage || props.isVisible)
    const wrapperClasses = computed(() =>
      props.isPage
        ? 'min-h-screen bg-gradient-to-b from-white to-light-purple/10 py-16 px-4 flex justify-center items-start'
        : 'fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4'
    )
    const cardClasses = computed(() =>
      props.isPage
        ? 'card-neumorphic max-w-4xl w-full shadow-xl'
        : 'card-neumorphic max-w-4xl w-full max-h-[90vh] overflow-y-auto'
    )

    if (props.isPage) {
      useSeo({
        title: 'Privacy Policy | Homvisit Kerala Data Practices Guide',
        description:
          'How Homvisit collects, uses, and protects personal data for corporate camp bookings, lab partners, and phlebotomist network applicants in Kerala.',
        path: '/privacy-policy',
        robots: 'index,follow',
        schema: graphSchema([
          organizationSchema(),
          breadcrumbSchema([
            { name: 'Home', path: '/' },
            { name: 'Privacy Policy', path: '/privacy-policy' },
          ]),
        ]),
      })
    }

    const closeModal = () => {
      emit('close')
    }

    const handleOverlayClick = () => {
      if (!props.isPage) {
        closeModal()
      }
    }

    return {
      lastUpdated,
      shouldRender,
      wrapperClasses,
      cardClasses,
      closeModal,
      handleOverlayClick
    }
  }
}
</script>
